import { useParams } from "@tanstack/react-router";
import { useEffect } from "react";

import { useDataSource } from "@/adapters/data-source";

/**
 * `/docs/attachments/:id` — the relative link a "Copy link" writes into a
 * page's markdown (`modules/docs/screens/Attachments.tsx`, §7.8).
 *
 * A route of its own, not a literal link straight to the API, because the
 * API is a different origin from the SPA in every deployed environment
 * (`adapters/api/base.ts`'s own comment on `apiFetch`) — a plain relative
 * href would resolve against the SPA's own host, which serves static files,
 * not `/api`. The hosting rewrite that sends every unknown path to
 * `index.html` (`firebase.json`) is what lets that relative link land here
 * at all; this component's only job is bouncing the browser on to the real
 * download URL. That still carries the session: the auth cookie's domain
 * spans both hosts (`env.ts`'s own comment on `authCookieDomain`), so a
 * plain top-level navigation sends it exactly as a `fetch` with
 * `credentials: "include"` would.
 *
 * A full navigation (`window.location`), not a client-side fetch-and-save:
 * the download route answers with `Content-Disposition: attachment`, which
 * is what makes a plain navigation trigger the browser's own save flow
 * without this component reading the bytes into memory just to hand them
 * back out.
 */
export function AttachmentRedirect({ id }: { id: string }) {
  const data = useDataSource();

  useEffect(() => {
    window.location.replace(data.docs.attachmentUrl(id));
  }, [id, data]);

  return (
    <div className="text-muted-foreground flex h-full items-center justify-center p-8 text-sm">
      Preparing your download…
    </div>
  );
}

/**
 * `router.tsx`'s `component` for this route — reads the `:id` and renders
 * `AttachmentRedirect` above it.
 *
 * `strict: false` rather than `useParams({ from: "/docs/attachments/$id" })`:
 * `router.tsx`'s route tree mixes in `sectionRoutes()`'s dynamically
 * generated `AnyRoute[]` (`config/modules.ts` is the source of truth, not a
 * literal route per section), which erases literal-path inference for the
 * whole tree — every typed `from` lookup resolves to `any` today, not just
 * this one. `strict: false` opts out of that lookup entirely rather than
 * assigning a value TypeScript cannot actually check.
 */
export function AttachmentRedirectRoute() {
  const params: Record<string, string | undefined> = useParams({
    strict: false,
  });
  return <AttachmentRedirect id={params.id ?? ""} />;
}
